import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';
import MetadataCard from '../components/MetadataCard';
import { fetchRecipeFile } from '../utils/recipeFetcher';
import { parseRecipeFile, formatCategoryName } from '../utils/recipeParser';
import '../styles/recipes.css';

/**
 * RecipeDetailPage displays a single recipe with metadata, image, and formatted instructions.
 * Fetches the recipe markdown file on demand and parses it at runtime.
 * Uses PageTemplate with dynamic title and icon="fas fa-utensils".
 * Route: /recipes/:category/:slug
 */
const RecipeDetailPage = () => {
  const { category, slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    fetchRecipeFile(category, slug)
      .then(markdown => {
        const parsed = parseRecipeFile(markdown, `${slug}.md`, category);
        if (parsed.type) {
          throw new Error(parsed.message || 'Failed to parse recipe');
        }
        setRecipe(parsed);
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [category, slug]);

  if (loading) {
    return (
      <PageTemplate title="Family Recipes" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-loading-state" role="status" aria-label="Loading recipe">
            <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            <p>Loading recipe...</p>
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (error) {
    if (error.status === 404) {
      return (
        <PageTemplate title="Recipe Not Found" icon="fas fa-utensils">
          <div className="section">
            <div className="recipe-not-found">
              <h2>Recipe not found</h2>
              <p>The recipe you are looking for does not exist.</p>
              <Link to={`/recipes/${category}`}>Back to {formatCategoryName(category)}</Link>
            </div>
          </div>
        </PageTemplate>
      );
    }

    return (
      <PageTemplate title="Error" icon="fas fa-utensils">
        <div className="section">
          <div className="recipe-error-state">
            <h2>Unable to load recipe</h2>
            <p>Something went wrong while loading this recipe. Please try again later.</p>
            <Link to={`/recipes/${category}`}>Back to {formatCategoryName(category)}</Link>
          </div>
        </div>
      </PageTemplate>
    );
  }

  const { metadata, htmlContent } = recipe;
  const hasImage = metadata.image && metadata.image.trim().length > 0;

  const renderImage = () => {
    if (!hasImage || imageError) {
      return (
        <div className="recipe-image-placeholder">
          <i className="fas fa-image" aria-hidden="true"></i>
        </div>
      );
    }

    return (
      <img
        className="recipe-image"
        src={metadata.image}
        alt={metadata.title}
        loading="lazy"
        style={{ maxWidth: '100%' }}
        onError={() => setImageError(true)}
      />
    );
  };

  const renderBody = () => {
    if (!htmlContent || !htmlContent.trim()) {
      return (
        <p className="empty-state-message">
          No instructions available for this recipe
        </p>
      );
    }

    return (
      <div
        className="recipe-body"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  return (
    <PageTemplate title={metadata.title} icon="fas fa-utensils">
      <div className="section recipe-detail-section">
        <div className="recipe-back-nav">
          <Link to={`/recipes/${category}`} className="recipe-back-link">
            <i className="fas fa-arrow-left" aria-hidden="true"></i> Back to {formatCategoryName(category)}
          </Link>
        </div>
        <MetadataCard
          prepTime={metadata.prepTime}
          cookTime={metadata.cookTime}
          servings={metadata.servings}
          difficulty={metadata.difficulty}
        />
        <div className="recipe-image-container">
          {renderImage()}
        </div>
        {renderBody()}
      </div>
    </PageTemplate>
  );
};

export default RecipeDetailPage;
